import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import axios from 'axios';
import { API_URL } from '../config';

// Types
type User = {
  id: string;
  email: string;
  name: string;
  first_name?: string;
  last_name?: string;
  phone?: string;
  role: string;
  token: string;
  profile_image?: string | null;
  preferences?: Record<string, any>;
};

type AuthContextType = {
  currentUser: User | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  updateCurrentUser: (userData: Partial<User>) => void;
  isAuthenticated: boolean;
  loading: boolean;
  error: string | null;
  avatarTimestamp: number;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Créer une instance d'Axios avec la configuration CORS
const api = axios.create({
  baseURL: API_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  }
});

// Intercepteur pour gérer les erreurs CORS
api.interceptors.response.use(
  response => response,
  error => {
    if (error.response) {
      // La requête a été faite et le serveur a répondu avec un statut hors 2xx
      console.error('Erreur de réponse:', error.response.status, error.response.data);
    } else if (error.request) {
      // La requête a été faite mais aucune réponse n'a été reçue
      console.error('Pas de réponse du serveur:', error.request);
    } else {
      // Une erreur s'est produite lors de la configuration de la requête
      console.error('Erreur de configuration:', error.message);
    }
    return Promise.reject(error);
  }
);

// Création du fournisseur d'authentification
export function AuthProvider({ children }: { children: ReactNode }) {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [avatarTimestamp, setAvatarTimestamp] = useState<number>(Date.now());

  // Vérifier l'état de connexion au chargement
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const token = localStorage.getItem('token');
        if (token) {
          // Vérifier si le token est valide en faisant une requête à l'API
          try {
            // Pour l'instant, si un token existe, on considère l'utilisateur comme connecté
            // TODO: Implémenter /auth/me dans l'API pour valider le token
            
            // Décoder le JWT pour extraire les informations de base
            const payload = JSON.parse(atob(token.split('.')[1]));
            
            // 🔒 RESTRICTION: Bloquer l'accès au dashboard pour les clients et techniciens
            if (payload.role === 'customer' || payload.role === 'technician') {
              console.log('Accès refusé: rôle non autorisé pour le dashboard');
              localStorage.removeItem('token');
              localStorage.removeItem('currentUser');
              setLoading(false);
              return;
            }
            
            // Pour la session persistée, on tente de relire toutes les données utilisateur depuis localStorage
            const storedUser = localStorage.getItem('currentUser');
            let additionalUserData: any = {};
            if (storedUser) {
              try {
                additionalUserData = JSON.parse(storedUser);
              } catch {}
            }
            const fallbackName = payload.email 
              ? payload.email.split('@')[0] 
              : (payload.phone || additionalUserData.phone || 'Utilisateur');
            const user = {
              id: payload.id.toString(),
              email: payload.email || '',
              name: additionalUserData.name || fallbackName,
              first_name: additionalUserData.first_name || '',
              last_name: additionalUserData.last_name || '',
              phone: additionalUserData.phone || payload.phone || '',
              role: payload.role,
              token: token,
              profile_image: additionalUserData.profile_image,
              preferences: additionalUserData.preferences || {}
            };
            setCurrentUser(user);
            console.log('Utilisateur authentifié:', user);
          } catch (apiError) {
            console.log('Token invalide ou API indisponible, suppression du token');
            localStorage.removeItem('token');
          }
        }
      } catch (err) {
        console.error('Erreur de vérification de l\'authentification:', err);
        localStorage.removeItem('token');
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, []);

  // Fonction de connexion
  const login = async (email: string, password: string): Promise<void> => {
    setLoading(true);
    setError(null);
    
    try {
      console.log('Tentative de connexion avec l\'email:', email);
      
      // Utiliser l'API réelle pour la connexion
      const response = await api.post('/auth/login', {
        email,
        password
      });
      
      if (response.data.success) {
        const { user, accessToken } = response.data.data;
        
        console.log('🔐 Rôle de l\'utilisateur:', user.role);
        
        // 🔒 RESTRICTION: Bloquer l'accès au dashboard pour les clients et techniciens
        if (user.role === 'customer' || user.role === 'technician') {
          const errorMsg = 'Accès refusé. Le tableau de bord est réservé aux administrateurs et managers. Veuillez utiliser l\'application mobile. Elle est disponible sur App Store et Google Play Store.';
          console.error('🚫 ' + errorMsg);
          setError(errorMsg);
          setLoading(false);
          throw new Error(errorMsg);
        }
        
        // Stocker le token dans le localStorage
        localStorage.setItem('token', accessToken);
        
        // Mettre à jour l'utilisateur actuel avec les vraies données de l'API
        const computedName = (user.first_name || user.last_name)
          ? `${user.first_name || ''} ${user.last_name || ''}`.trim()
          : user.profile
            ? `${user.profile.first_name || ''} ${user.profile.last_name || ''}`.trim()
            : user.email
              ? user.email.split('@')[0]
              : (user.phone || 'Utilisateur');

        const authUser = {
          id: user.id.toString(),
          email: user.email || '',
          name: computedName,
          first_name: user.first_name || user.profile?.first_name || '',
          last_name: user.last_name || user.profile?.last_name || '',
          phone: user.phone || '',
          role: user.role,
          token: accessToken,
          profile_image: user.profile_image || null,
          preferences: user.preferences || {}
        };
        setCurrentUser(authUser);
        // Stocker la photo de profil dans localStorage pour la session persistée
        localStorage.setItem('currentUser', JSON.stringify(authUser));
  console.log("Connexion réussie avec l'API:", authUser);
      } else {
        throw new Error(response.data.message || 'Échec de la connexion');
      }
      
    } catch (err: any) {
      let errorMessage = 'Échec de la connexion. Veuillez réessayer plus tard.';
      
      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      console.error('Erreur de connexion:', err);
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Fonction de déconnexion
  const logout = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('currentUser');
    setCurrentUser(null);
    // La redirection sera gérée par le composant qui appelle logout
  }, []);

  // Fonction pour mettre à jour l'utilisateur actuel
  const updateCurrentUser = useCallback((userData: Partial<User>) => {
    if (currentUser) {
      const updatedUser = { ...currentUser, ...userData };
      
      // Recalculer le nom complet si first_name ou last_name sont fournis
      if (userData.first_name !== undefined || userData.last_name !== undefined) {
        const firstName = updatedUser.first_name || '';
        const lastName = updatedUser.last_name || '';
        if (firstName && lastName) {
          updatedUser.name = `${firstName} ${lastName}`;
        } else if (firstName || lastName) {
          updatedUser.name = firstName || lastName;
        }
      }
      
      // Si profile_image est mis à jour, forcer le rechargement de l'image
      if (userData.profile_image !== undefined) {
        setAvatarTimestamp(Date.now());
      }
      
      setCurrentUser(updatedUser);
      localStorage.setItem('currentUser', JSON.stringify(updatedUser));
    }
  }, [currentUser]);

  const value = {
    currentUser,
    login,
    logout,
    updateCurrentUser,
    isAuthenticated: !!currentUser,
    loading,
    error,
    avatarTimestamp
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}

// Hook personnalisé pour utiliser le contexte d'authentification
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth doit être utilisé à l\'intérieur d\'un AuthProvider');
  }
  return context;
}

